
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  Star,
  ArrowRight,
  MapPin
} from 'lucide-react';
import AssessmentCalendarBooking from '@/components/AssessmentCalendarBooking';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';

type DiagnosticYearBucket = 'early' | 'middle' | 'upper' | 'secondary';
type DiagnosticCourseId = 'selective' | 'sec' | 'foundations' | 'naplan' | 'general';

interface DiagnosticPayload {
  childName: string | null;
  yearLevel: string;
  yearBucket: DiagnosticYearBucket;
  courseOfInterest: string;
  courseId: DiagnosticCourseId;
  centre: string;
  source: 'diagnostic';
}

interface BookAssessmentProps {
  initialCentre?: string;
}

interface PrefillFormData {
  childName?: string;
  courseInterested?: string;
  currentGrade?: string;
}

const BookAssessment = ({ initialCentre }: BookAssessmentProps) => {
  const [searchParams] = useSearchParams();
  const {
    selectedState: ctxSelectedState,
    selectedCenter: ctxSelectedCenter,
    setSelectedState: setCtxSelectedState,
    setSelectedCenter: setCtxSelectedCenter,
  } = useLocationContext();

  const [selectedBookingState, setSelectedBookingState] = useState<string>('');
  const [selectedCenter, setSelectedCenter] = useState<string>('');
  const [showStatePopup, setShowStatePopup] = useState(false);
  const [showCenterPopup, setShowCenterPopup] = useState(false);
  const [tempSelectedState, setTempSelectedState] = useState<string>('');
  const [tempSelectedCenter, setTempSelectedCenter] = useState<string>('');
  const [prefillFormData, setPrefillFormData] = useState<PrefillFormData | undefined>();
  const [bookingSource, setBookingSource] = useState<string>('direct');

  // Has the initial prefill resolution already run?
  const initialResolvedRef = useRef(false);

  // Available states for booking
  const availableStates = [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'WA', name: 'Western Australia' }
  ];

  // NSW centers
  const nswCenters = [
    { slug: 'colebee', name: 'Colebee' },
    { slug: 'penrith', name: 'Penrith' },
    { slug: 'riverstone', name: 'Riverstone' },
    { slug: 'the-ponds', name: 'The Ponds' }
  ];

  // VIC centers
  const vicCenters = [
    { slug: 'caroline-springs', name: 'Caroline Springs' },
    { slug: 'point-cook', name: 'Point Cook' },
    { slug: 'tarneit', name: 'Tarneit' },
    { slug: 'truganina', name: 'Truganina' },
    { slug: 'werribee', name: 'Werribee' }
  ];

  // WA centers
  const waCenters = [
    { slug: 'canning-vale', name: 'Canning Vale' }
  ];

  // Centre slug -> { name, state } lookup for known bookable centres
  const knownCentres = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; state: string }>();
    nswCenters.forEach(c => map.set(c.slug, { ...c, state: 'NSW' }));
    vicCenters.forEach(c => map.set(c.slug, { ...c, state: 'VIC' }));
    waCenters.forEach(c => map.set(c.slug, { ...c, state: 'WA' }));
    return map;
  }, []);

  // Get centers based on selected state
  const getCentersForState = (stateCode: string) => {
    if (stateCode === 'NSW') return nswCenters;
    if (stateCode === 'VIC') return vicCenters;
    if (stateCode === 'WA') return waCenters;
    return [];
  };

  // Apply a resolved centre slug: preselect, skip popups, suppress global location popup,
  // and sync LocationContext so subsequent navigation stays consistent.
  const applyResolvedCentre = (slug: string, prefill?: PrefillFormData) => {
    const match = knownCentres.get(slug);
    if (!match) {
      console.warn(
        `[BookAssessment] Unknown centre slug "${slug}" - falling back to manual centre selection.`
      );
      return false;
    }

    setSelectedBookingState(match.state);
    setSelectedCenter(match.slug);
    setTempSelectedState(match.state);
    setTempSelectedCenter(match.slug);
    setShowStatePopup(false);
    setShowCenterPopup(false);

    if (prefill) {
      setPrefillFormData(prev => ({ ...prev, ...prefill }));
    }

    // Suppress the global location popup for this and future visits
    try {
      localStorage.setItem('schoolola_location_selected', 'true');
    } catch {
      /* ignore storage errors */
    }

    // Update LocationContext so header/footer reflect the deep-linked centre.
    // URL/prop wins over any stale context value.
    if (ctxSelectedState !== match.state) {
      setCtxSelectedState(match.state);
    }
    if (ctxSelectedCenter !== match.slug) {
      setCtxSelectedCenter(match.slug);
    }

    return true;
  };

  // Initial resolution: URL ?centre= → initialCentre prop. (CustomEvent handled separately.)
  useEffect(() => {
    if (initialResolvedRef.current) return;

    const urlCentre = searchParams.get('centre');
    const candidate = urlCentre || initialCentre || '';

    if (candidate) {
      const ok = applyResolvedCentre(candidate);
      initialResolvedRef.current = true;
      if (!ok) {
        // Fall through to manual popup flow
        setShowStatePopup(true);
      }
    } else {
      initialResolvedRef.current = true;
      setShowStatePopup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CustomEvent listener - highest-priority handoff from future diagnostic component.
  useEffect(() => {
    const handler = (event: Event) => {
      const ce = event as CustomEvent<DiagnosticPayload>;
      const detail = ce.detail;
      if (!detail || typeof detail.centre !== 'string') {
        console.warn('[BookAssessment] schoolola:diagnostic-complete fired without valid detail payload.');
        return;
      }

      const prefill: PrefillFormData = {};
      if (detail.childName) prefill.childName = detail.childName;
      if (detail.courseOfInterest) prefill.courseInterested = detail.courseOfInterest;
      if (detail.yearLevel) prefill.currentGrade = detail.yearLevel;

      setBookingSource(detail.source || 'diagnostic');
      applyResolvedCentre(detail.centre, prefill);
    };

    window.addEventListener('schoolola:diagnostic-complete', handler as EventListener);
    return () => window.removeEventListener('schoolola:diagnostic-complete', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxSelectedState, ctxSelectedCenter]);

  // Handle state selection from popup
  const handleStateSelection = () => {
    if (tempSelectedState) {
      setShowStatePopup(false);
      setShowCenterPopup(true);
    }
  };

  // Handle center selection
  const handleCenterSelection = () => {
    if (tempSelectedCenter) {
      setSelectedCenter(tempSelectedCenter);
      setSelectedBookingState(tempSelectedState);
      setShowCenterPopup(false);
      try {
        localStorage.setItem('schoolola_location_selected', 'true');
      } catch {
        /* ignore */
      }
    }
  };

  // Get selected center name for display
  const getSelectedCenterName = () => {
    const allCenters = [...nswCenters, ...vicCenters, ...waCenters];
    return allCenters.find(c => c.slug === selectedCenter)?.name || '';
  };

  const assessmentFeatures = [
    'Comprehensive academic evaluation',
    'Personalised learning plan',
    'Subject-specific recommendations',
    'Goal setting and pathway planning',
    'No obligation consultation'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-accent-50 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-accent text-white px-4 py-2 text-lg">
              Free Assessment
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900">
              Book Your Free Assessment
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Discover your child's strengths, identify areas for improvement, and create a personalised learning plan - completely free with no obligation.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Assessment Information */}
            <div className="lg:col-span-1 space-y-8">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-heading text-gray-900">
                    What's Included
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {assessmentFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-heading text-gray-900">
                    Assessment Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">Duration</div>
                      <div className="text-gray-600">30 minutes</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Star className="h-5 w-5 text-accent" />
                    <div>
                      <div className="font-medium">Investment</div>
                      <div className="text-primary font-semibold">Completely FREE</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-primary/5 to-accent/5">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <h3 className="font-heading font-semibold text-gray-900">
                      Ready to Get Started?
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Take the first step towards your child's academic success
                    </p>
                    <Link to="/programs">
                      <Button className="w-full bg-primary hover:bg-primary/90">
                        Explore Our Programmes
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Booking Widget */}
            <div className="lg:col-span-2">
              {selectedBookingState && selectedCenter ? (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl font-heading text-gray-900">
                      Schedule Your Assessment
                    </CardTitle>
                    <div className="flex items-center justify-between bg-gradient-to-r from-primary/5 to-accent/5 p-4 rounded-lg border">
                      <p className="text-gray-600">
                        Booking at <span className="font-medium">{getSelectedCenterName()}</span>, {selectedBookingState}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBookingState('');
                          setSelectedCenter('');
                          setShowStatePopup(true);
                          setTempSelectedState('');
                          setTempSelectedCenter('');
                        }}
                      >
                        Change Centre
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <AssessmentCalendarBooking
                      centerSlug={selectedCenter}
                      hideHeader
                      initialFormData={prefillFormData}
                      source={bookingSource}
                    />

                  </CardContent>
                </Card>
              ) : (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-20 text-center">
                    <div className="space-y-4">
                      <MapPin className="h-12 w-12 text-gray-400 mx-auto" />
                      <h3 className="text-xl font-heading font-semibold text-gray-900">
                        Select Your State
                      </h3>
                      <p className="text-gray-600">
                        Please select your state to view available assessment appointments.
                      </p>
                      <Button onClick={() => setShowStatePopup(true)}>
                        Choose State
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* State Selection Popup */}
      <Dialog open={showStatePopup} onOpenChange={setShowStatePopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <MapPin className="h-6 w-6 text-primary" />
                <span>Select Your State</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-center text-gray-600">
              Please select your state to book your free assessment consultation.
            </p>
            
            <RadioGroup
              value={tempSelectedState}
              onValueChange={setTempSelectedState}
              className="space-y-4"
            >
              {availableStates.map((state) => (
                <div key={state.code} className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem 
                    value={state.code} 
                    id={state.code}
                    className="border-primary"
                  />
                  <Label 
                    htmlFor={state.code}
                    className="text-gray-700 cursor-pointer font-medium flex-1"
                  >
                    {state.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <Button 
              className="w-full"
              onClick={handleStateSelection}
              disabled={!tempSelectedState}
            >
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Center Selection Popup */}
      <Dialog open={showCenterPopup} onOpenChange={setShowCenterPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <MapPin className="h-6 w-6 text-primary" />
                <span>Select Your Centre</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-center text-gray-600">
              Please select your nearest Schoolola centre.
            </p>
            
            <RadioGroup
              value={tempSelectedCenter}
              onValueChange={setTempSelectedCenter}
              className="space-y-3 max-h-80 overflow-y-auto"
            >
              {getCentersForState(tempSelectedState).map((center) => (
                <div key={center.slug} className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem 
                    value={center.slug} 
                    id={center.slug}
                    className="border-primary"
                  />
                  <Label 
                    htmlFor={center.slug}
                    className="text-gray-700 cursor-pointer font-medium flex-1"
                  >
                    {center.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCenterPopup(false);
                  setShowStatePopup(true);
                  setTempSelectedCenter('');
                }}
              >
                Back
              </Button>
              <Button 
                className="flex-1"
                onClick={handleCenterSelection}
                disabled={!tempSelectedCenter}
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Why Choose Schoolola Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Why Choose Schoolola?
              </h2>
              <p className="text-xl text-gray-600">
                Join thousands of families who trust Schoolola for their child's educational journey
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900">
                    Proven Results
                  </h3>
                  <p className="text-gray-600">
                    Our students consistently achieve outstanding results in OC and Selective tests
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
                    <Star className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900">
                    Expert Teachers
                  </h3>
                  <p className="text-gray-600">
                    Qualified educators with extensive experience in NSW curriculum
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900">
                    Personalised Approach
                  </h3>
                  <p className="text-gray-600">
                    Tailored learning plans designed to meet each student's unique needs
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookAssessment;
